import ImageModal from "components/ImageModal";
import { useTooltip } from "context/TooltipProvider";
import imageVector from "images/imageVector.png";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { editSuspect2 } from "services/lealIntityService";
import {
  CaseCreation,
  FILE_URL,
  Reporter,
  Suspect,
  Victim,
  Witness,
} from "types";
import {
  constructFileUrl,
  errorHandler,
  errorHandlerOnForm,
} from "utils/helper";

type Props = {
  caseData: CaseCreation;
  caseRelatedParties: {
    suspects: Suspect[];
    witnesses: Witness[];
    victims: Victim[];
    reporters: Reporter[];
  };
  fetchData: () => void;
  isCoordinator?: boolean;
  isRegistrar?: boolean;
  isProsecutor?: boolean;
};

const ClosedSusectsList: FC<Props> = ({
  caseData,
  caseRelatedParties,
  fetchData,
  isProsecutor = false,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [selectedSuspect, setSelectedSuspect] = useState<Suspect>();
  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-2 h-8 bg-red-600 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t("ioc_closed_suspects_title")}
            </h3>
          </div>
        </div>

        <div className="space-y-4">
          {caseRelatedParties?.suspects
            ?.filter((s) => s?.closed_by_42)
            .map((suspect, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl shadow-sm p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <img
                      src={(() => {
                        const doc = suspect?.documents?.find(
                          (doc) => doc.document_type === "suspect_picture",
                        );
                        return doc
                          ? constructFileUrl(doc, FILE_URL.SUSPECT)
                          : imageVector;
                      })()}
                      alt={t("ioc_suspect_image_alt")}
                      className="w-12 h-12 rounded-lg object-cover cursor-pointer"
                      onClick={() => {
                        setSelectedSuspect(suspect);
                        setIsImageModalOpen(true);
                      }}
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {`${suspect?.first_name} ${suspect?.middle_name} ${suspect?.last_name}`}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <span>{suspect?.age}</span>
                        <span>•</span>
                        <span>{suspect?.education_level}</span>
                        <span>•</span>
                        <span>{suspect?.nationality}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-sm text-gray-700">
                  <p>
                    <span className="font-semibold">
                      {t("ioc_closure_reason")}:
                    </span>{" "}
                    {suspect?.reason_for_closure || t("na")}
                  </p>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setSelectedSuspect(suspect);
                      setIsOpenDetail(true);
                    }}
                    className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    {t("view_details_button")}
                  </button>

                  {(caseData?.status === "Pending" ||
                    caseData?.status === "Respond_Back_prosecutor" ||
                    caseData.is_case_updated_by_court) && (
                    <button
                      onClick={async () => {
                        setLoading(true);
                        editSuspect2({
                          uu_id: suspect?.uu_id,
                          reason_for_closure: "",
                          is_defendant: false,
                          closed_by_42: false,
                        })
                          .then(() => {
                            setLoading(false);
                            showTooltip(t("successful"), "success");
                            fetchData();
                          })
                          .catch((error) => {
                            setLoading(false);
                            showTooltip(
                              error?.response?.data?.detail ||
                                errorHandlerOnForm(error) ||
                                errorHandler(error) ||
                                t("something_went_wrong_please_try_again"),
                              "error",
                            );
                          });
                      }}
                      className="px-4 py-2 text-sm text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={loading}
                    >
                      {t("ioc_remove_suspect")}
                    </button>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>

      {isOpenDetail && (
        <SuspectDetailInfo
          suspect={selectedSuspect}
          isOpen={isOpenDetail}
          onClose={() => setIsOpenDetail(false)}
        />
      )}
      {isImageModalOpen && (
        <ImageModal
          isOpen={isImageModalOpen}
          imageSrc={(() => {
            const doc = selectedSuspect?.documents?.find(
              (doc) => doc.document_type === "suspect_picture",
            );
            return doc ? constructFileUrl(doc, FILE_URL.SUSPECT) : imageVector;
          })()}
          label={
            selectedSuspect?.first_name +
            " " +
            selectedSuspect?.last_name +
            " " +
            selectedSuspect?.last_name
          }
          onClose={() => setIsImageModalOpen(false)}
        />
      )}
    </div>
  );
};

export default ClosedSusectsList;
